import {Injectable} from '@angular/core';
import {VideoAsset} from '@app/asset';
import {Slot} from '@app/review/models';
import {TimeRange} from '@app/review/timerange';
import {TimeScale} from '@app/review/timescale';
import {BehaviorSubject, Observable, Subject} from 'rxjs';

export enum Source {
    All,
    Video,
    Timeline,
}

export class VideoState {
    isMuted: boolean;
    isLooping: boolean;
    volume: number;
    source: Source;
    slot: Slot = Slot.Primary;
}

export class PlaybackState {
    frame: number;
    isPlaying: boolean;
    frameCount: number;
    frameRate: number;
    playRange: TimeRange;
    source: Source;
    slot: Slot = Slot.Primary;

    private _timeScale: TimeScale;

    constructor(asset: VideoAsset) {
        this.frame = 0;
        this.isPlaying = false;
        this.frameCount = 0;
        this.frameRate = 0;
        this.playRange = null;
        this.source = Source.All;
        this._timeScale = null;

        if (asset !== null) {
            this.frameRate = asset.framerate;
            this.frameCount = Math.round(asset.framerate * asset.duration);
            this.playRange = new TimeRange(null, null);
        }
    }

    get timeScale(): TimeScale {
        if (this._timeScale === null) {
            this._timeScale = new TimeScale(this.frameCount, this.frameRate);
        }
        return this._timeScale;
    }
}

function newVideoState(slot: Slot): VideoState {
    return {
        isMuted: true,
        isLooping: true,
        volume: 0,
        source: Source.All,
        slot,
    };
}

// Keyed by Slot (Primary/Compare) so a single-Asset Review (just Primary) and a two-Asset compare both
// go through the same service instance - see docs/design/review-compare-assets.md §3.2. Subjects emit one
// slot's state at a time, tagged with `slot`; consumers (components bound to a given pane) filter to their
// own slot the same way they already filter out their own `source` to avoid feedback loops.
@Injectable({
    providedIn: 'root',
})
export class ReviewVideoService {
    public videoState$: Observable<VideoState>;
    public playbackState$: Observable<PlaybackState>;
    public buffered$: Observable<TimeRanges>;

    private _videoStateSubject: BehaviorSubject<VideoState>;
    private _playbackSubject: BehaviorSubject<PlaybackState>;
    private _bufferedSubject: Subject<TimeRanges>;

    // State, per slot
    private _videoStates: Map<Slot, VideoState>;
    private _playbackStates: Map<Slot, PlaybackState>;

    constructor() {
        this._videoStates = new Map<Slot, VideoState>([
            [Slot.Primary, newVideoState(Slot.Primary)],
            [Slot.Compare, newVideoState(Slot.Compare)],
        ]);
        this._playbackStates = new Map<Slot, PlaybackState>([
            [Slot.Primary, new PlaybackState(null)],
            [Slot.Compare, new PlaybackState(null)],
        ]);
        this._playbackStates.get(Slot.Compare).slot = Slot.Compare;

        this._videoStateSubject = new BehaviorSubject<VideoState>(this._videoStates.get(Slot.Primary));
        this.videoState$ = this._videoStateSubject.asObservable();
        this._playbackSubject = new BehaviorSubject<PlaybackState>(this._playbackStates.get(Slot.Primary));
        this.playbackState$ = this._playbackSubject.asObservable();
        this._bufferedSubject = new Subject();
        this.buffered$ = this._bufferedSubject.asObservable();
    }

    // The subjects only replay the most recent emission, which is often the *other* slot's (e.g. Compare's
    // setAsset() lands right after Primary's) - a pane subscribing late would then filter that out and never
    // see its own initial state (leaving e.g. <video loop> unset). These let a subscriber seed with its own.
    public getVideoState(slot: Slot): VideoState {
        return this._videoStates.get(slot);
    }

    public getPlaybackState(slot: Slot): PlaybackState {
        return this._playbackStates.get(slot);
    }

    public setAsset(asset: VideoAsset, frame: number = 0, slot: Slot = Slot.Primary) {
        const videoState = newVideoState(slot);
        this._videoStates.set(slot, videoState);

        const playbackState = new PlaybackState(asset);
        playbackState.slot = slot;
        playbackState.frame = frame;
        this._playbackStates.set(slot, playbackState);

        this._videoStateSubject.next(videoState);
        this._playbackSubject.next(playbackState);
    }

    public setFrame(frame: number, source: Source, slot: Slot = Slot.Primary): void {
        const playbackState = this._playbackStates.get(slot);
        playbackState.frame = frame;
        playbackState.source = source;
        this._playbackSubject.next(playbackState);
    }

    public setBuffered(timeRanges: TimeRanges) {
        this._bufferedSubject.next(timeRanges);
    }

    public play(source: Source, value: boolean = null, slot: Slot = Slot.Primary): void {
        const playbackState = this._playbackStates.get(slot);
        if (value === null) {
            playbackState.isPlaying = !playbackState.isPlaying;
        } else {
            playbackState.isPlaying = value;
        }

        playbackState.source = source;
        this._playbackSubject.next(playbackState);
    }

    public mute(source: Source, value: boolean = null, slot: Slot = Slot.Primary): void {
        const videoState = this._videoStates.get(slot);
        if (value === null) {
            videoState.isMuted = !videoState.isMuted;
        } else if (videoState.isMuted === value) {
            // Don't send state changes when the value is the same
            return;
        } else {
            videoState.isMuted = value;
        }
        videoState.source = source;
        this._videoStateSubject.next(videoState);
    }

    public setVolume(value: number, slot: Slot = Slot.Primary): void {
        const videoState = this._videoStates.get(slot);
        videoState.volume = value;
        this._videoStateSubject.next(videoState);
    }

    public loop(source: Source, value: boolean = null, slot: Slot = Slot.Primary) {
        const videoState = this._videoStates.get(slot);
        if (value === null) {
            videoState.isLooping = !videoState.isLooping;
        } else if (videoState.isLooping === value) {
            // Don't send state changes when the value is the same
            return;
        } else {
            videoState.isLooping = value;
        }
        videoState.source = source;
        this._videoStateSubject.next(videoState);
    }

    setRange(range: number[], source: Source, slot: Slot = Slot.Primary) {
        const playbackState = this._playbackStates.get(slot);
        if (playbackState.playRange === null) {
            // No video asset loaded - there is no range to clear or set (e.g. a synced client sitting on an
            // image asset receiving the asset-switch PlayRange reset).
            return;
        }
        playbackState.playRange.fromArray(range);
        playbackState.source = source;
        this._playbackSubject.next(playbackState);
    }
}
